// MJ-393 — bounded exponential backoff for the Cloudflare Workers AI proxy.
//
// Kimi K2.6 (`@cf/moonshotai/kimi-k2.6`) is the proven tool-caller for this
// agent, but it carries a ~60 req/min per-model ceiling on Cloudflare Workers
// AI. Under realistic load a burst of calls trips HTTP 429 and, without a
// seatbelt, kills the agent thread mid-answer. This helper wraps the completion
// fetch so a transient 429 is retried a few times with growing delays instead
// of aborting the turn.
//
// Pure, dependency-free ESM so it can be bundled into the browser build by Vite
// AND imported directly by the Node test (`scripts/test-backoff.mjs`) — one
// source of truth, no drift. Types live in the co-located `backoff.d.ts`.

/**
 * Backoff delay for a zero-based retry attempt: 500ms → 1s → 2s → …
 * @param {number} attempt zero-based retry index (0 = first retry)
 * @param {number} [baseDelayMs=500]
 * @returns {number} delay in milliseconds
 */
export function computeBackoffDelay(attempt, baseDelayMs = 500) {
	return baseDelayMs * 2 ** attempt;
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Call `doFetch` and, if it resolves to an HTTP 429, retry up to `maxRetries`
 * times with exponential backoff (500ms → 1s → 2s). Returns the first
 * non-429 response, or the final 429 response once retries are exhausted (the
 * caller surfaces a graceful "busy, retry" message rather than a raw crash).
 *
 * Only 429 is retried — every other status (including other errors) is returned
 * immediately so real failures are not masked. Network rejections propagate.
 *
 * @param {() => Promise<Response>} doFetch performs one fetch attempt
 * @param {{ maxRetries?: number, baseDelayMs?: number, sleep?: (ms:number)=>Promise<void>, onRetry?: (info:{attempt:number, delayMs:number})=>void }} [opts]
 * @returns {Promise<Response>}
 */
export async function retryOn429(doFetch, opts = {}) {
	const {
		maxRetries = 3,
		baseDelayMs = 500,
		sleep = defaultSleep,
		onRetry,
	} = opts;
	let attempt = 0;
	// eslint-disable-next-line no-constant-condition
	while (true) {
		const res = await doFetch();
		if (res.status !== 429 || attempt >= maxRetries) return res;
		const delayMs = computeBackoffDelay(attempt, baseDelayMs);
		if (onRetry) onRetry({ attempt, delayMs });
		await sleep(delayMs);
		attempt++;
	}
}
